import React, { useState } from 'react';
import { MessageSquare, Send, Mail, X, CheckCircle2 } from 'lucide-react';
import { supabase } from '../lib/supabase.ts';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail?: string;
  username?: string;
  userName?: string;
  userUid?: string;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  userEmail = '',
  username = '',
  userName = '',
  userUid,
}) => {
  const [feedbackType, setFeedbackType] = useState<
    'suggestion' | 'scripture' | 'bug' | 'encouragement'
  >('suggestion');
  const [message, setMessage] = useState('');
  const [senderEmail, setSenderEmail] = useState(userEmail);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (!isOpen) return null;

  const typeLabels = {
    suggestion: 'Feature Suggestion',
    scripture: 'Scripture Reference / Question Correction',
    bug: 'Bug Report / Display Issue',
    encouragement: 'Word of Encouragement / Praise',
  };

  const recipientEmail = 'adonizchoy@gmail.com';

  const mailtoSubject = encodeURIComponent(
    `[Quick!Bible Feedback] ${typeLabels[feedbackType]}`
  );
  const mailtoBody = encodeURIComponent(
    `Scholar: ${username || 'Anonymous'}\nFeedback Type: ${typeLabels[feedbackType]}\n\nMessage:\n${message}\n\nSent from Quick!Bible Web App`
  );
  const mailtoHref = `mailto:${recipientEmail}?subject=${mailtoSubject}&body=${mailtoBody}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      setErrorMessage('Please enter your feedback message.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      // Direct insert into Supabase table 'feedback'
      const { data, error } = await supabase.from('feedback').insert([
        {
          category: feedbackType,
          email: senderEmail.trim() || null,
          message: trimmedMessage,
        },
      ]);

      if (error) {
        console.error('[Feedback] Supabase insert error:', error);
        setErrorMessage(
          error.message ||
            'Failed to insert feedback in Supabase. You may also use Open in Email App.'
        );
        setIsSent(false);
      } else {
        setIsSent(true);
        setMessage('');
        setTimeout(() => {
          setIsSent(false);
          onClose();
        }, 2500);
      }
    } catch (err: any) {
      console.error('[Feedback] Submission exception:', err);
      setErrorMessage(
        err?.message || 'An unexpected error occurred while sending feedback.'
      );
      setIsSent(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-xs"
    >
      <div className="w-full max-w-lg rounded-2xl bg-[#141414] border border-[#262626] p-5 sm:p-6 shadow-2xl space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[#262626] pb-3">
          <div className="flex items-center gap-2.5">
            <img
              src="/quickibible-icon.png"
              alt="QuickiBible"
              className="w-9 h-9 rounded-xl object-contain shadow-md border border-amber-500/30 shrink-0"
            />
            <div>
              <h2
                id="feedback-modal-title"
                className="text-base font-bold text-[#FAFAFA]"
              >
                Send Feedback to Creator
              </h2>
              <p className="text-xs text-[#737373] text-[#A3A3A3]">
                Delivered directly to Adonis Melencion ({recipientEmail})
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close feedback modal"
            className="p-1 rounded-lg text-[#A3A3A3] hover:text-[#FAFAFA] hover:bg-[#1C1C1C] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {isSent ? (
          <div className="py-8 text-center space-y-2">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto animate-bounce" />
            <h3 className="text-base font-bold text-[#FAFAFA]">
              Thank You for Your Feedback!
            </h3>
            <p className="text-xs text-[#737373] text-[#A3A3A3] max-w-sm mx-auto">
              Your message has been sent to Adonis Melencion at {recipientEmail}. We appreciate your support in making Quick!Bible better for scholars worldwide.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Feedback category picker */}
            <div>
              <label className="block text-xs font-semibold text-[#D4D4D4] mb-1.5">
                Feedback Category
              </label>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {(
                  [
                    { id: 'suggestion', label: 'Suggestion' },
                    { id: 'scripture', label: 'Bible Scripture' },
                    { id: 'bug', label: 'Bug / Issue' },
                    { id: 'encouragement', label: 'Encouragement' },
                  ] as const
                ).map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setFeedbackType(cat.id)}
                    className={`px-3 py-2 rounded-xl border text-left font-medium transition-colors ${
                      feedbackType === cat.id
                        ? 'border-[#525252] bg-[#141414] text-[#FAFAFA] font-semibold'
                        : 'border-[#262626] bg-[#1C1C1C] text-[#D4D4D4] hover:border-[#262626]'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Optional Sender Email */}
            <div>
              <label
                htmlFor="feedback-email"
                className="block text-xs font-semibold text-[#D4D4D4] mb-1"
              >
                Your Email (Optional, if you wish to receive a reply)
              </label>
              <input
                id="feedback-email"
                type="email"
                placeholder="your-email@quickibible.com"
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value)}
                className="w-full rounded-xl border border-[#262626] bg-[#1C1C1C] px-3.5 py-2 text-xs text-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#525252]"
              />
            </div>

            {/* Message Area */}
            <div>
              <label
                htmlFor="feedback-message"
                className="block text-xs font-semibold text-[#D4D4D4] mb-1"
              >
                Your Message
              </label>
              <textarea
                id="feedback-message"
                required
                rows={4}
                placeholder="Share your thoughts, scripture corrections, suggestions, or words of encouragement..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full rounded-xl border border-[#262626] bg-[#1C1C1C] p-3 text-xs text-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#525252]"
              />
            </div>

            {errorMessage && (
              <div
                role="alert"
                className="p-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-xs text-red-400"
              >
                {errorMessage}
              </div>
            )}

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 pt-2 border-t border-[#262626]">
              <a
                href={mailtoHref}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium text-[#737373] text-[#A3A3A3] hover:text-[#FAFAFA] hover:text-[#FAFAFA] hover:bg-[#1C1C1C] transition-colors"
                title="Send via your default email application"
              >
                <Mail className="w-3.5 h-3.5 text-[#A3A3A3]" />
                <span>Open in Email App</span>
              </a>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 rounded-xl border border-[#262626] text-xs font-semibold text-[#D4D4D4] hover:bg-[#1C1C1C] transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !message.trim()}
                  data-testid="submit-feedback-button"
                  className="inline-flex items-center justify-center gap-1.5 px-5 py-2 rounded-xl bg-[#525252] hover:bg-[#404040] disabled:opacity-50 text-white text-xs font-semibold transition-colors"
                >
                  <Send className="w-3.5 h-3.5" />
                  <span>{isSubmitting ? 'Sending...' : 'Send Feedback'}</span>
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
